/**
 * WorkspaceCategorySelect - Componente de seleção de categoria de workspace
 * 
 * Select com ícones Icofont para escolha de categoria de workspace.
 * Exibe ícone + nome de cada categoria com validação de obrigatoriedade.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useCrmWorkspaceCategories, CrmWorkspaceCategory } from '@/hooks/useCrmWorkspaceCategories';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * Props do componente WorkspaceCategorySelect
 */
interface WorkspaceCategorySelectProps {
  /** ID da categoria selecionada */
  value: string;
  /** Callback ao selecionar categoria */
  onChange: (categoryId: string) => void;
  /** Se está desabilitado */
  disabled?: boolean;
  /** Mostrar label */
  showLabel?: boolean;
  /** Classe CSS adicional */
  className?: string;
  /** Mostrar erro de validação */
  error?: boolean;
  /** Mensagem de erro */
  errorMessage?: string;
}

/**
 * Componente de ícone Icofont
 * Renderiza ícone usando classes do Icofont
 */
function IcofontIcon({ icon, className }: { icon: string; className?: string }) {
  return (
    <i className={cn(icon, className)} aria-hidden="true" />
  );
}

/**
 * Componente para exibir item de categoria com ícone
 */
function CategoryItem({ category }: { category: CrmWorkspaceCategory }) {
  return (
    <div className="flex items-center gap-2">
      <IcofontIcon icon={category.icon} className="text-base text-muted-foreground" />
      <span>{category.name}</span>
    </div>
  );
}

/**
 * Componente principal de seleção de categoria
 */
export function WorkspaceCategorySelect({
  value,
  onChange,
  disabled = false,
  showLabel = true,
  className,
  error = false,
  errorMessage,
}: WorkspaceCategorySelectProps) {
  const { t } = useTranslation('crm');
  const { data: categories, isLoading } = useCrmWorkspaceCategories();

  // Categoria selecionada para exibição no trigger
  const selectedCategory = useMemo(() => {
    if (!value || !categories) return null;
    return categories.find(c => c.id === value);
  }, [value, categories]);

  return (
    <div className={cn('space-y-2', className)}>
      {showLabel && (
        <Label 
          htmlFor="category-select"
          className={cn(error && 'text-destructive')}
        >
          {t('workspaces.category', 'Categoria')}
          <span className="text-destructive ml-0.5">*</span>
        </Label>
      )}
      
      <Select 
        value={value} 
        onValueChange={onChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger 
          id="category-select"
          className={cn(
            'w-full',
            error && 'border-destructive focus:ring-destructive'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-muted-foreground">
                {t('common.loading', 'Carregando...')}
              </span>
            </div>
          ) : selectedCategory ? (
            <CategoryItem category={selectedCategory} />
          ) : (
            <span className="text-muted-foreground">
              {t('workspaces.selectCategory', 'Selecione uma categoria')}
            </span>
          )}
        </SelectTrigger>
        
        <SelectContent>
          {categories?.map(category => (
            <SelectItem 
              key={category.id} 
              value={category.id}
              className="cursor-pointer"
            >
              <CategoryItem category={category} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error && errorMessage && (
        <p className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
